import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import PitchMode from "../../../PitchMode";

const API = process.env.REACT_APP_BACKEND_URL;

const brandLockup = (
  <div className="flex flex-col items-center gap-4">
    <img
      src="https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/h1sxdkcz_Aeroplay%20logo%202026_White.png"
      alt="Aeroplay"
      style={{ height: 96, width: "auto" }}
    />
  </div>
);

const AEROPLAY_MARK_DARK =
  "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/h1sxdkcz_Aeroplay%20logo%202026_White.png";
const AEROPLAY_MARK_LIGHT =
  "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/oot4cqoo_Aeroplay%20logo%202026_Black.png";

const LoginScreen = ({ code, onSuccess, error, setError, submitting, setSubmitting }) => {
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/pitch/${code}/brand`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setBrand)
      .catch(() => {});
  }, [code]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/pitch/${code}/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.status === 429) {
        setError("Too many attempts. Please try again in a few minutes.");
      } else if (!res.ok) {
        setError("Incorrect password.");
      } else {
        onSuccess();
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: "var(--k-canvas)" }}>
      <div className="w-full max-w-[420px]">
        <div className="mb-12 flex items-center justify-center gap-5">
          <img src={AEROPLAY_MARK_LIGHT} alt="Aeroplay" style={{ height: 40, width: "auto" }} />
          {brand?.logo && (
            <>
              <span className="w-px h-8 bg-black/15" aria-hidden="true" />
              <img
                src={brand.logo}
                alt={brand.name || code}
                className={brand.backdrop ? "rounded-lg px-3 py-2" : ""}
                style={{ height: brand.backdrop ? 44 : 36, width: "auto", maxWidth: 220, backgroundColor: brand.backdrop ? "#1d1d1f" : undefined }}
                data-testid="login-airline-logo"
              />
            </>
          )}
        </div>

        <div className="text-center mb-10">
          <h1 className="text-[36px] font-semibold tracking-tight leading-tight" style={{ color: "#1d1d1f" }}>
            Access your<br />presentation.
          </h1>
          <p className="mt-3 text-[15px]" style={{ color: "rgba(0,0,0,0.55)" }}>
            Enter the password shared by your Aeroplay representative.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="pitch-password-input"
              placeholder="Password"
              aria-label="Password"
              className="w-full h-12 rounded-xl bg-white border border-black/15 focus:border-[#1d1d1f] focus:ring-4 focus:ring-black/[0.06] outline-none px-4 pr-16 text-[15px] transition-all"
              style={{ color: "#1d1d1f" }}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[12px] font-semibold uppercase tracking-wider hover:opacity-80"
              style={{ color: "rgba(0,0,0,0.5)" }}
              data-testid="pitch-password-toggle"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? "Hide" : "Show"}
            </button>
          </div>
          {error && (
            <div
              className="text-[14px] text-rose-600 flex items-center gap-2"
              role="alert"
              data-testid="pitch-password-error"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-600" /> {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="pitch-password-submit"
            className="w-full h-12 rounded-full font-semibold text-[15px] transition-all hover:scale-[1.005] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#1d1d1f", color: "#ffffff" }}
          >
            {submitting ? "Signing in…" : "Continue"}
          </button>
        </form>

        <p className="text-center text-[13px] mt-10" style={{ color: "rgba(0,0,0,0.45)" }}>
          Not the right link?{" "}
          <a href="/" className="underline underline-offset-2 hover:text-[#1d1d1f]">Go to the public pitch</a>
        </p>
      </div>
    </div>
  );
};

const PitchAirlineRoute = () => {
  const { code: rawCode } = useParams();
  const code = (rawCode || "").toUpperCase();
  const [state, setState] = useState({ status: "checking", airline: null });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadData = async () => {
    try {
      const res = await fetch(`${API}/api/pitch/${code}/data`, { credentials: "include" });
      if (res.ok) {
        const json = await res.json();
        setState({ status: "ready", airline: json.config });
      } else if (res.status === 401) {
        setState({ status: "login", airline: null });
      } else if (res.status === 404) {
        setState({ status: "notfound", airline: null });
      } else {
        setState({ status: "error", airline: null });
      }
    } catch {
      setState({ status: "error", airline: null });
    }
  };

  useEffect(() => {
    if (!code || !/^[A-Z0-9]{2,3}$/.test(code)) {
      setState({ status: "notfound", airline: null });
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (!code || state.status === "notfound") {
    return <Navigate to="/pitch" replace />;
  }

  if (state.status === "checking") {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center" style={{ backgroundColor: "var(--k-canvas)" }}>
        <div className="text-[13px]" style={{ color: "rgba(0,0,0,0.5)" }}>Loading…</div>
      </div>
    );
  }

  if (state.status === "login") {
    return (
      <LoginScreen
        code={code}
        onSuccess={loadData}
        error={error}
        setError={setError}
        submitting={submitting}
        setSubmitting={setSubmitting}
      />
    );
  }

  if (state.status === "ready" && state.airline) {
    return <PitchMode airline={state.airline} />;
  }

  return (
    <div className="min-h-screen w-screen flex flex-col items-center justify-center px-6 text-center" style={{ backgroundColor: "var(--k-canvas)" }}>
      <img src={AEROPLAY_MARK_LIGHT} alt="Aeroplay" style={{ height: 40, width: "auto", marginBottom: 40 }} />
      <div className="text-[24px] font-semibold mb-2 tracking-tight" style={{ color: "#1d1d1f" }}>Presentation unavailable</div>
      <p className="text-[15px] max-w-sm" style={{ color: "rgba(0,0,0,0.55)" }}>
        This link is temporarily unavailable. Please try again in a few minutes or contact your Aeroplay account manager.
      </p>
      <a href="/" className="mt-10 text-[13px] underline underline-offset-2" style={{ color: "rgba(0,0,0,0.5)" }}>Go to the public pitch</a>
    </div>
  );
};

export default PitchAirlineRoute;
